import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const industries = [
{
  id: "customer-service",
  name: "Customer Service",
  icon: "🛎️",
  description: "Transform customer support with AI-powered automation and virtual assistants.",
  benefits: [
  "24/7 customer support availability",
  "Automated ticket routing and prioritization",
  "Reduced response times and wait periods",
  "Improved customer satisfaction scores"],

  useCases: [
  "AI chatbot for common customer inquiries",
  "Automated email response system",
  "Customer sentiment analysis",
  "Self-service knowledge base assistant"],

  image: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "healthcare",
  name: "Healthcare",
  icon: "🏥",
  description: "AI automation solutions for medical practices, hospitals, and healthcare providers.",
  benefits: [
  "24/7 patient support via AI chatbots",
  "Automated appointment scheduling and reminders",
  "Patient data collection and pre-screening",
  "Medical record summaries and analysis"],

  useCases: [
  "Virtual health assistant for patient inquiries",
  "Prescription refill automation",
  "Appointment follow-up system",
  "Insurance verification automation"],

  image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "recruitment",
  name: "Recruitment",
  icon: "👔",
  description: "Streamline hiring processes and improve candidate experiences with AI automation.",
  benefits: [
  "Automated resume screening and ranking",
  "Candidate communication and updates",
  "Interview scheduling automation",
  "Onboarding process management"],

  useCases: [
  "AI-powered job posting analyzer",
  "Candidate qualification chatbot",
  "Automated reference checking",
  "Interview feedback collection"],

  image: "https://images.unsplash.com/photo-1519305124423-5ccccff55da9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "real-estate",
  name: "Real Estate",
  icon: "🏠",
  description: "Enhance property management and sales with intelligent automation solutions.",
  benefits: [
  "Automated property inquiry responses",
  "24/7 availability for client questions",
  "Lead qualification and prioritization",
  "Streamlined showing scheduling"],

  useCases: [
  "Property viewing scheduler",
  "Rental application processor",
  "Client matching with properties",
  "Market analysis automation"],

  image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "retail",
  name: "Retail",
  icon: "🛍️",
  description: "Enhance customer experiences and streamline operations for retail businesses.",
  benefits: [
  "Personalized shopping recommendations",
  "Automated inventory management",
  "Enhanced customer support",
  "Optimized pricing and promotions"],

  useCases: [
  "Virtual shopping assistant",
  "Product recommendation engine",
  "Demand forecasting system",
  "Customer loyalty program automation"],

  image: "https://images.unsplash.com/photo-1534452203293-494d7ddbf7e0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "education",
  name: "Education",
  icon: "🎓",
  description: "Transform learning experiences and administrative processes for educational institutions.",
  benefits: [
  "24/7 student support and assistance",
  "Automated administrative processes",
  "Personalized learning experiences",
  "Enhanced student engagement"],

  useCases: [
  "AI tutor for student questions",
  "Automated grading and feedback",
  "Course recommendation system",
  "Enrollment and admissions automation"],

  image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "automotive",
  name: "Automotive",
  icon: "🚗",
  description: "Accelerate sales and service for car dealerships and automotive businesses.",
  benefits: [
  "Lead qualification and management",
  "Automated service scheduling",
  "24/7 customer support",
  "Vehicle inventory management"],

  useCases: [
  "Service appointment scheduler",
  "Vehicle recommendation system",
  "Parts inventory automation",
  "Customer follow-up system"],

  image: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "financial-services",
  name: "Financial Services",
  icon: "💰",
  description: "Enhance customer experience and streamline operations for banks and financial institutions.",
  benefits: [
  "24/7 customer service and support",
  "Fraud detection and prevention",
  "Automated loan application processing",
  "Enhanced financial advisory services"],

  useCases: [
  "Banking virtual assistant",
  "Transaction anomaly detection",
  "Automated credit scoring",
  "Financial planning chatbot"],

  image: "https://images.unsplash.com/photo-1601597111158-2fceff292cdc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "transportation-logistics",
  name: "Transportation & Logistics",
  icon: "🚚",
  description: "Optimize operations and enhance customer service for logistics and transportation companies.",
  benefits: [
  "Route optimization and planning",
  "Automated shipping updates",
  "Inventory and warehouse management",
  "Enhanced customer communication"],

  useCases: [
  "Delivery tracking assistant",
  "Fleet management system",
  "Warehouse automation",
  "Supply chain optimization"],

  image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "manufacturing",
  name: "Manufacturing",
  icon: "🏭",
  description: "Streamline production processes and enhance supply chain management for manufacturers.",
  benefits: [
  "Predictive maintenance alerts",
  "Quality control automation",
  "Supply chain optimization",
  "Production scheduling enhancements"],

  useCases: [
  "Equipment maintenance chatbot",
  "Production anomaly detection",
  "Inventory forecasting system",
  "Supply chain tracking assistant"],

  image: "https://images.unsplash.com/photo-1509791650328-319264fd4d4a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3MTg3MTl8MHwxfHNlYXJjaHwxfHxBJTIwbWFudWZhY3R1cmluZyUyMGZhY2lsaXR5JTIwc2hvd2Nhc2luZyUyMG1hY2hpbmVyeSUyMGFuZCUyMHByb2R1Y3Rpb24lMjBsaW5lcy58ZW58MHx8fHwxNzQ3NzExNDYxfDA&ixlib=rb-4.1.0&q=80&w=200$w=800"
},
{
  id: "energy-utilities",
  name: "Energy & Utilities",
  icon: "⚡",
  description: "Enhance service delivery and operational efficiency for energy and utility companies.",
  benefits: [
  "Smart grid management",
  "Predictive maintenance",
  "Customer service automation",
  "Usage monitoring and optimization"],

  useCases: [
  "Energy consumption advisor",
  "Outage reporting and tracking",
  "Smart meter data analysis",
  "Service request automation"],

  image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "telecommunications",
  name: "Telecommunications",
  icon: "📞",
  description: "Optimize customer service and network management for telecommunication providers.",
  benefits: [
  "Automated customer support",
  "Network monitoring and troubleshooting",
  "Service activation automation",
  "Churn prediction and prevention"],

  useCases: [
  "Technical support chatbot",
  "Service plan recommendation engine",
  "Network issue detection",
  "Customer retention assistant"],

  image: "https://images.unsplash.com/photo-1534951009808-766178b47a4f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "construction",
  name: "Construction",
  icon: "🚧",
  description: "Enhance project management and customer communication for construction companies.",
  benefits: [
  "Project timeline optimization",
  "Material inventory management",
  "Client communication automation",
  "Safety compliance assistance"],

  useCases: [
  "Project status tracking assistant",
  "Safety compliance chatbot",
  "Material ordering automation",
  "Subcontractor coordination system"],

  image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "insurance",
  name: "Insurance",
  icon: "💷",
  description: "Streamline policy management and claims processing for insurance companies.",
  benefits: [
  "Automated claims processing",
  "Policy recommendation engine",
  "Customer service automation",
  "Fraud detection and prevention"],

  useCases: [
  "Claims submission assistant",
  "Policy comparison chatbot",
  "Risk assessment automation",
  "Customer inquiry handling"],

  image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "pharmaceutical",
  name: "Pharmaceutical",
  icon: "💊",
  description: "Enhance research, development, and customer interaction for pharmaceutical companies.",
  benefits: [
  "Clinical trial data analysis",
  "Research information management",
  "Patient support automation",
  "Regulatory compliance assistance"],

  useCases: [
  "Drug information assistant",
  "Patient support chatbot",
  "Research data analysis",
  "Adverse event reporting system"],

  image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "food-processing",
  name: "Food Processing",
  icon: "🍟",
  description: "Optimize production and quality control for food processing businesses.",
  benefits: [
  "Quality assurance automation",
  "Supply chain management",
  "Recipe optimization",
  "Compliance monitoring"],

  useCases: [
  "Quality control assistant",
  "Inventory tracking system",
  "Nutritional analysis automation",
  "Production scheduling optimization"],

  image: "https://images.unsplash.com/photo-1511690656952-34342bb7c2f2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3MTg3MTl8MHwxfHNlYXJjaHwxfHxBJTIwY2xvc2UtdXAlMjBpbWFnZSUyMG9mJTIwYSUyMGZvb2QlMjBwcm9jZXNzaW5nJTIwZmFjaWxpdHklMjBzaG93Y2FzaW5nJTIwbWFjaGluZXJ5JTIwYW5kJTIwcHJvZHVjdGlvbiUyMGxpbmVzLnxlbnwwfHx8fDE3NDc3MTE0NTR8MA&ixlib=rb-4.1.0&q=80&w=200$w=800"
},
{
  id: "government-services",
  name: "Government Services",
  icon: "🏢",
  description: "Enhance citizen services and administrative processes for government agencies.",
  benefits: [
  "Citizen query automation",
  "Document processing efficiency",
  "Service request management",
  "Internal process optimization"],

  useCases: [
  "Citizen information chatbot",
  "Form processing automation",
  "Service request tracking",
  "Public information dissemination"],

  image: "https://images.unsplash.com/photo-1524668951403-d44b28200ce0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},
{
  id: "mining-resources",
  name: "Mining & Resources",
  icon: "⛏️",
  description: "Enhance operational efficiency and safety for mining and resource extraction companies.",
  benefits: [
  "Equipment monitoring automation",
  "Safety protocol management",
  "Supply chain optimization",
  "Environmental compliance tracking"],

  useCases: [
  "Equipment maintenance assistant",
  "Safety compliance chatbot",
  "Production tracking system",
  "Environmental reporting automation"],

  image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3MTg3MTl8MHwxfHNlYXJjaHwxfHxBJTIwc2NlbmljJTIwdmlldyUyMG9mJTIwYSUyMG1pbmluZyUyMGFuZCUyMHJlc291cmNlcyUyMG9wZXJhdGlvbiUyMHNpdGUlMkMlMjBzaG93Y2FzaW5nJTIwbWFjaGluZXJ5JTIwYW5kJTIwbmF0dXJhbCUyMHN1cnJvdW5kaW5ncy58ZW58MHx8fHwxNzQ3NzQxMDMxfDA&ixlib=rb-4.1.0&q=80&w=200$w=600"
},
{
  id: "entertainment-media",
  name: "Entertainment & Media",
  icon: "🎬",
  description: "Enhance content creation and audience engagement for media and entertainment companies.",
  benefits: [
  "Content recommendation engine",
  "Audience analysis automation",
  "Content moderation assistance",
  "Production workflow optimization"],

  useCases: [
  "Content recommendation system",
  "Viewer support chatbot",
  "Content metadata generation",
  "Audience engagement analysis"],

  image: "https://images.unsplash.com/photo-1493804714600-6edb1cd93080?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "cybersecurity",
  name: "Cybersecurity",
  icon: "🔒",
  description: "Enhance threat detection and security operations for businesses and organizations.",
  benefits: [
  "Threat detection automation",
  "Security incident response",
  "User authentication assistance",
  "Security training enhancement"],

  useCases: [
  "Security alert triage",
  "Phishing detection assistant",
  "Security policy chatbot",
  "Vulnerability assessment system"],

  image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "marketing",
  name: "Marketing",
  icon: "📈",
  description: "Enhance campaign performance and content creation with AI-powered automation.",
  benefits: [
  "Automated content generation",
  "Social media management",
  "Campaign analytics and optimization",
  "Personalized customer outreach"],

  useCases: [
  "Social media content calendar",
  "Email marketing automation",
  "Ad copy generator",
  "Customer segmentation system"],

  image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
},
{
  id: "hospitality",
  name: "Hospitality",
  icon: "🏨",
  description: "Transform guest experiences for hotels, gyms, and hospitality businesses.",
  benefits: [
  "24/7 guest services and support",
  "Automated booking and reservations",
  "Personalized guest recommendations",
  "Feedback collection and analysis"],

  useCases: [
  "Hotel concierge chatbot",
  "Gym membership management",
  "Restaurant reservation system",
  "Guest experience personalization"],

  image: "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
}];


export default function IndustriesPage() {
  const navigate = useNavigate();
  // Function to handle hash change and scroll to the element
  const scrollToHashElement = () => {
    const hash = window.location.hash;
    if (hash) {
      // Remove the # symbol to get the ID
      const id = hash.substring(1);
      // Wait a bit for the DOM to be ready
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  // Handle initial load and hash changes
  useEffect(() => {
    // Set page title
    document.title = "Industries We Serve | AI Automation Co.";

    // Initial scroll to hash element if present
    scrollToHashElement();

    // Add event listener for hash changes
    window.addEventListener('hashchange', scrollToHashElement);

    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener('hashchange', scrollToHashElement);
    };
  }, []);

  return (
    <Layout data-id="cm6qvjnpp" data-path="src/pages/IndustriesPage.tsx">
      {/* Header section */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-16" data-id="73vrli5z2" data-path="src/pages/IndustriesPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="c2ua1x999" data-path="src/pages/IndustriesPage.tsx">
          <div className="max-w-3xl mx-auto text-center" data-id="q6phr80u2" data-path="src/pages/IndustriesPage.tsx">
            <h1 className="text-4xl font-bold text-gray-900 mb-6" data-id="nhukgi9d1" data-path="src/pages/IndustriesPage.tsx">
              AI Automation for Every Industry
            </h1>
            <p className="text-lg text-gray-600 mb-8" data-id="1pcsp59na" data-path="src/pages/IndustriesPage.tsx">
              We provide tailored automation solutions for businesses across different sectors.
              Explore how our AI technology can transform operations in your industry.
            </p>
          </div>
        </div>
      </div>
      
      {/* Industries overview */}
      <div className="py-16 bg-white" data-id="wvh4klpim" data-path="src/pages/IndustriesPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="yskbx9lim" data-path="src/pages/IndustriesPage.tsx">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-8 mb-16" data-id="emlmu907k" data-path="src/pages/IndustriesPage.tsx">
            {industries.map((industry) =>
            <Card key={industry.id} className="border-0 hover:shadow-md transition-shadow" data-id="kcgyaaf9h" data-path="src/pages/IndustriesPage.tsx">
                <CardContent className="p-6 text-center" data-id="nk2o4mhmx" data-path="src/pages/IndustriesPage.tsx">
                  <div className="text-5xl mb-4" data-id="z9jvau2mm" data-path="src/pages/IndustriesPage.tsx">{industry.icon}</div>
                  <h3 className="text-lg font-medium text-gray-900" data-id="qhq0247cg" data-path="src/pages/IndustriesPage.tsx">
                    <a href={`#${industry.id}`} className="hover:text-blue-600 transition-colors" data-id="l9dkc4diy" data-path="src/pages/IndustriesPage.tsx">
                      {industry.name}
                    </a>
                  </h3>
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* Detailed industry sections */}
          {industries.map((industry, index) =>
          <div key={industry.id} id={industry.id} className={`py-16 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-id="05rm29m64" data-path="src/pages/IndustriesPage.tsx">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="afgne9ghf" data-path="src/pages/IndustriesPage.tsx">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" style={{ minHeight: '600px' }} data-id="st2vmveqk" data-path="src/pages/IndustriesPage.tsx">
                <div className={index % 2 === 0 ? 'order-1 lg:order-1' : 'order-1 lg:order-2'} style={{ minHeight: '500px' }} data-id="mz6lcgzf8" data-path="src/pages/IndustriesPage.tsx">
                  <div className="mb-2 text-4xl" data-id="lvxh4oqoj" data-path="src/pages/IndustriesPage.tsx">{industry.icon}</div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="68e60nxzu" data-path="src/pages/IndustriesPage.tsx">
                    {industry.name}
                  </h2>
                  <p className="text-lg text-gray-600 mb-8" data-id="feq613po9" data-path="src/pages/IndustriesPage.tsx">
                    {industry.description}
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="z7mbnv4hz" data-path="src/pages/IndustriesPage.tsx">
                    <div data-id="7kcofjfuh" data-path="src/pages/IndustriesPage.tsx">
                      <h3 className="text-lg font-medium text-gray-900 mb-3" data-id="4whu69ifu" data-path="src/pages/IndustriesPage.tsx">Key Benefits</h3>
                      <ul className="space-y-2" data-id="fe2h6z3f0" data-path="src/pages/IndustriesPage.tsx">
                        {industry.benefits.map((benefit, idx) =>
                        <li key={idx} className="flex items-start" data-id="058vxihh0" data-path="src/pages/IndustriesPage.tsx">
                            <div className="text-green-500 mr-2" data-id="64w2c6hb9" data-path="src/pages/IndustriesPage.tsx">✓</div>
                            <span className="text-gray-600" data-id="zy7k5ebqv" data-path="src/pages/IndustriesPage.tsx">{benefit}</span>
                          </li>
                        )}
                      </ul>
                    </div>
                    
                    <div data-id="z6ugtkgex" data-path="src/pages/IndustriesPage.tsx">
                      <h3 className="text-lg font-medium text-gray-900 mb-3" data-id="w4f9cqzm9" data-path="src/pages/IndustriesPage.tsx">Popular Use Cases</h3>
                      <ul className="space-y-2" data-id="l7n3jk4lq" data-path="src/pages/IndustriesPage.tsx">
                        {industry.useCases.map((useCase, idx) =>
                        <li key={idx} className="flex items-start" data-id="bhe5w9n1p" data-path="src/pages/IndustriesPage.tsx">
                            <div className="text-blue-500 mr-2" data-id="2hnkm9xby" data-path="src/pages/IndustriesPage.tsx">•</div>
                            <span className="text-gray-600" data-id="oro5d97ug" data-path="src/pages/IndustriesPage.tsx">{useCase}</span>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  
                  <Button asChild className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" data-id="md3pw33u7" data-path="src/pages/IndustriesPage.tsx">
                    <Link to={`/contact?industry=${industry.id}`} className="flex items-center" data-id="qo1znkb86" data-path="src/pages/IndustriesPage.tsx">
                      Discuss Your {industry.name} Solution
                      <ArrowRight className="ml-2 h-4 w-4" data-id="8wx1inc3l" data-path="src/pages/IndustriesPage.tsx" />
                    </Link>
                  </Button>
                </div>
                
                <div className={index % 2 === 0 ? 'order-2 lg:order-2' : 'order-2 lg:order-1'} style={{ minHeight: '500px' }} data-id="4vfsd9zb3" data-path="src/pages/IndustriesPage.tsx">
                  <div className="relative rounded-xl overflow-hidden shadow-xl" data-id="y0xrwtaj7" data-path="src/pages/IndustriesPage.tsx">
                    <img
                      src={industry.image}
                      alt={`${industry.name} Automation`}
                      className="w-full h-auto" data-id="pys02q1d8" data-path="src/pages/IndustriesPage.tsx" />

                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/30 to-purple-600/30 mix-blend-overlay" data-id="ak4q1t9ke" data-path="src/pages/IndustriesPage.tsx"></div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-8" data-id="cyad4g273" data-path="src/pages/IndustriesPage.tsx">
                <Button
                  variant="outline"
                  className="flex items-center text-gray-600 hover:text-blue-600"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} data-id="xcnompzxq" data-path="src/pages/IndustriesPage.tsx">
                  Back to Top
                </Button>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
      
      {/* CTA section */}
      <div className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white" data-id="vp94nylgb" data-path="src/pages/IndustriesPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="gvlyngmms" data-path="src/pages/IndustriesPage.tsx">
          <div className="max-w-3xl mx-auto text-center" data-id="3bv74gnu5" data-path="src/pages/IndustriesPage.tsx">
            <h2 className="text-3xl font-bold mb-6" data-id="oudcwgqux" data-path="src/pages/IndustriesPage.tsx">
              Ready to Transform Your Industry with AI?
            </h2>
            <p className="text-lg text-blue-100 mb-8" data-id="wr49takdc" data-path="src/pages/IndustriesPage.tsx">
              Our team of experts will work with you to develop a custom solution that addresses your specific industry challenges.
            </p>
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-blue-50" data-id="erypmiyzu" data-path="src/pages/IndustriesPage.tsx">
              <Link to="/contact" data-id="tgppxtcyz" data-path="src/pages/IndustriesPage.tsx">Book a Free Consultation</Link>
            </Button>
          </div>
        </div>
      </div>
    </Layout>);

}